import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';
import { chartColors } from '../../helpers/chartColors';
import NoChartData from './NoChartData';

export const RTFOCommandCenterTonnesPerDayChart = ({ reportDate = new Date(), gridView = true }) => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const data = useMemo(() => {
    if (!currentRTFO?.stageData) return [];

    return getChartData();

    function getChartData() {
      try {
        const startTimestamp = new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.[0]]?.stageStartTimestamp)?.getTime();
        const endTimestamp = new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageEndTimestamp)?.getTime();

        const numDays = Math.ceil(Math.max(1, (endTimestamp - startTimestamp) / 1000 / 60 / 60 / 24));

        const traces = [];
        const X = [];
        const YTonnesPerDay = [];

        for (let i = 0; i < numDays; i++) {
          const date = new Date(startTimestamp + i * 24 * 60 * 60 * 1000);
          const day = date.getDate();
          const month = date.getMonth();

          const dailyStageNames = currentRTFO?.stages?.filter(
            s =>
              new Date(currentRTFO?.stageData?.[s]?.stageStartTimestamp)?.getMonth() === month &&
              new Date(currentRTFO?.stageData?.[s]?.stageStartTimestamp)?.getDate() === day
          );

          let totalTonnes = 0;

          for (const stageName of dailyStageNames) {
            const stage = currentRTFO?.stageData?.[stageName];

            totalTonnes += stage?.metrics?.totalProppant;
          }

          YTonnesPerDay.push(totalTonnes);
          X.push(date?.toLocaleDateString());
        }

        // total pumping time trace
        traces.push({
          x: X,
          y: YTonnesPerDay,
          type: 'bar',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Tonnes per Day (ton/day)',
          marker: {
            color: chartColors[0]
          }
        });

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      }
    }
  }, [currentRTFO?.stageData, currentRTFO?.stages]);

  const layout = {
    barmode: 'group',
    yaxis: {
      // Main y axis
      linecolor: darkMode ? 'lightgrey' : chartColors[0],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : chartColors[0],
      title: {
        text: `Tonnes per Day (ton/day)`,
        font: {
          size: 12
        }
      },
      // autorange: true,
      side: 'left',
      uirevision: 'true',
      rangemode: 'nonnegative'
    },

    xaxis: {
      // Main x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      tickfont: {
        size: 10
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      type: 'category'
    },
    autosize: true,
    showlegend: true,
    legend: {
      orientation: 'h',
      yanchor: 'top',
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: -0.1
    },
    annotations: [
      {
        text: 'Tonnes Per Day',
        font: {
          size: 14,
          color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: gridView ? 1.1 : 1.06, //position in y domain
        xref: 'paper',
        yref: 'paper'
      },
      {
        text: `Pad: ${currentRTFO?.name}`,
        font: {
          size: 10,
          color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: gridView ? 1.05 : 1.035, //position in y domain
        xref: 'paper',
        yref: 'paper'
      }
    ],
    uirevision: 'true',
    margin: { t: 40, l: 60, r: 50, b: 0 },
    images: [
      {
        x: gridView ? 0.05 : 0.035,
        y: gridView ? 1.02 : 1.01,
        sizex: gridView ? 0.1 : 0.05,
        sizey: 0.1,
        source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: 'page',
        yref: 'y1 domain',
        xanchor: 'right',
        yanchor: 'bottom',
        opacity: 0.75
      }
    ],
    plot_bgcolor: 'transparent',
    paper_bgcolor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Daily Frac Summary-${new Date(reportDate).toISOString().split('T')[0]}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {data?.length > 0 ? (
        <Plot data={data} layout={layout} config={config} useResizeHandler={true} style={{ width: '100%', height: '100%' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <NoChartData />
        </div>
      )}
    </div>
  );
};
